import React, { useEffect, useState, useRef} from 'react';
import { useAuth } from '../../context/AuthContext'; // 导入 AuthContext
import axios from '../../axiosConfig'; // 修改为配置好的axios实例
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/UserAppointment.css'; // 确保有对应的CSS文件
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

// 安全格式化时间（支持字符串、毫秒/秒时间戳）
const formatDateSafe = (input) => {
  if (!input) return '';
  let d;
  if (typeof input === 'number') {
    // 10位秒级时间戳 → 毫秒
    d = new Date(input < 1e12 ? input * 1000 : input);
  } else {
    // 字符串或 Date
    const tryNum = Number(input);
    if (!Number.isNaN(tryNum) && tryNum !== 0) {
      d = new Date(tryNum < 1e12 ? tryNum * 1000 : tryNum);
    } else {
      d = new Date(input);
    }
  }
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

// 评论表单组件
const CommentForm = ({ bookingId, onCommentSubmitted, initialData, isEditing, onCancel }) => {
  const { t } = useTranslation();
  const [scores, setScores] = useState(initialData ? {
    scoreService: initialData.scoreService,
    scoreProfessional: initialData.scoreProfessional,
    scoreStrength: initialData.scoreStrength,
    scoreMinimalMovements: initialData.scoreMinimalMovements,
    scoreOverall: initialData.scoreOverall
  } : {
    scoreService: 5,
    scoreProfessional: 5,
    scoreStrength: 5,
    scoreMinimalMovements: 5,
    scoreOverall: 5
  });
  const [comment, setComment] = useState(initialData ? initialData.comment : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleScoreChange = (field, value) => {
    setScores({
      ...scores,
      [field]: parseInt(value)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      setError(t('table.noData'));
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    const commentData = {
      ...scores,
      comment
    };
    
    if (isEditing) {
      // 编辑现有评论
      commentData.id = initialData.id;
      
      axios.put(`/jiaolianyuyue/comment/update?userId=${user.zhanghao}`, commentData)
        .then(response => {
          if (response.data.code === 0) {
            onCommentSubmitted();
          } else {
            setError(response.data.msg || t('common.operationFailed'));
          }
          setSubmitting(false);
        })
        .catch(err => {
          console.error('评论更新出错:', err);
          setError(t('common.operationFailed'));
          setSubmitting(false);
        });
    } else {
      // 添加新评论
      commentData.bookingId = bookingId;
      commentData.userId = user.zhanghao;
      
      axios.post('/jiaolianyuyue/comment/add', commentData)
        .then(response => {
          if (response.data.code === 0) {
            setComment('');
            onCommentSubmitted();
          } else {
            setError(response.data.msg || t('common.operationFailed'));
          }
          setSubmitting(false);
        })
        .catch(err => {
          console.error('评论提交出错:', err);
          setError(t('common.operationFailed'));
          setSubmitting(false);
        });
    }
  };

  return (
    <div className="comment-form">
      <h3>{isEditing ? t('table.edit') : t('booking.comment')}</h3>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="rating-group">
          <div className="rating-item">
            <label>{t('table.status')}</label>
            <select value={scores.scoreService} onChange={(e) => handleScoreChange('scoreService', e.target.value)}>
              {[1,2,3,4,5].map(num => (
                <option key={`service-${num}`} value={num}>{num}</option>
              ))}
            </select>
          </div>
          <div className="rating-item">
            <label>{t('table.details')}</label>
            <select value={scores.scoreProfessional} onChange={(e) => handleScoreChange('scoreProfessional', e.target.value)}>
              {[1,2,3,4,5].map(num => (
                <option key={`professional-${num}`} value={num}>{num}</option>
              ))}
            </select>
          </div>
          <div className="rating-item">
            <label>{t('table.status')}</label>
            <select value={scores.scoreStrength} onChange={(e) => handleScoreChange('scoreStrength', e.target.value)}>
              {[1,2,3,4,5].map(num => (
                <option key={`strength-${num}`} value={num}>{num}</option>
              ))}
            </select>
          </div>
          <div className="rating-item">
            <label>{t('table.details')}</label>
            <select value={scores.scoreMinimalMovements} onChange={(e) => handleScoreChange('scoreMinimalMovements', e.target.value)}>
              {[1,2,3,4,5].map(num => (
                <option key={`movements-${num}`} value={num}>{num}</option>
              ))}
            </select>
          </div>
          <div className="rating-item">
            <label>{t('table.status')}</label>
            <select value={scores.scoreOverall} onChange={(e) => handleScoreChange('scoreOverall', e.target.value)}>
              {[1,2,3,4,5].map(num => (
                <option key={`overall-${num}`} value={num}>{num}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="comment-textarea">
          <label>{t('table.details')}</label>
          <textarea 
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('header.noResult')}
            rows={4}
            required
          />
        </div>
        <div className="form-buttons">
          {isEditing && (
            <button type="button" className="cancel-btn" onClick={onCancel}>
              <i className="fas fa-times"></i> {t('common.cancel')}
            </button>
          )}
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? <><i className="fas fa-spinner fa-spin"></i> {t('payment.processing')}</> : 
              isEditing ? <><i className="fas fa-check"></i> {t('table.modify')}</> : <><i className="fas fa-paper-plane"></i> {t('table.add')}</>}
          </button>
        </div>
      </form>
    </div>
  );
};

// 评论展示组件
const CommentDisplay = ({ comment, onDelete, onEdit, canManage }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { t } = useTranslation();
  
  const handleDelete = () => {
    if (confirmDelete) {
      onDelete(comment.id);
    } else {
      setConfirmDelete(true);
      // 3秒后自动重置确认状态
      setTimeout(() => setConfirmDelete(false), 3000);
    }
  };
  
  return (
    <div className="comment-display">
      <div className="comment-header">
        <div className="comment-status">
          {t('table.status')}: <span className={`status-${comment.isApproved === 1 ? 'approved' : 'pending'}`}>
            {comment.isApproved === 1 ? t('review.approved') : t('review.pending')}
          </span>
        </div>
        {canManage && (
          <div className="comment-actions">
            <button className="edit-btn" onClick={() => onEdit(comment)}>
              <i className="fas fa-edit"></i> {t('table.edit')}
            </button>
            <button className={`delete-btn ${confirmDelete ? 'confirm' : ''}`} onClick={handleDelete}>
              {confirmDelete ? t('common.confirmDelete') : <><i className="fas fa-trash"></i> {t('table.delete')}</>}
            </button>
          </div>
        )}
      </div>
      
      <div className="comment-rating">
        <div className="rating-stars">
          <div className="rating-item">
            <span className="rating-label">{t('review.serviceAttitude')}:</span>
            <span className="stars">{renderStars(comment.scoreService)}</span>
          </div>
          <div className="rating-item">
            <span className="rating-label">{t('review.professionalism')}:</span>
            <span className="stars">{renderStars(comment.scoreProfessional)}</span>
          </div>
          <div className="rating-item">
            <span className="rating-label">{t('review.strength')}:</span>
            <span className="stars">{renderStars(comment.scoreStrength)}</span>
          </div>
          <div className="rating-item">
            <span className="rating-label">{t('review.movementDetails')}:</span>
            <span className="stars">{renderStars(comment.scoreMinimalMovements)}</span>
          </div>
          <div className="rating-item">
            <span className="rating-label">{t('review.overall')}:</span>
            <span className="stars">{renderStars(comment.scoreOverall)}</span>
          </div>
        </div>
      </div>
      
      <div className="comment-content">
        <p>{comment.comment}</p>
      </div>
      
      <div className="comment-footer">
        <div className="comment-time">
          {formatDateSafe(comment.createTime)}
        </div>
      </div>
    </div>
  );
};

// 辅助函数：渲染星星
const renderStars = (score) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    if (i < score) {
      stars.push(<i key={i} className="fas fa-star filled"></i>);
    } else {
      stars.push(<i key={i} className="far fa-star"></i>);
    }
  }
  return stars;
};

// 退款申请表单组件
const RefundForm = ({ bookingId, price, onRefundSubmitted, onCancel }) => {
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState(price || 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const { t } = useTranslation();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError(t('booking.refundReasonPlaceholder'));
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    const params = new URLSearchParams();
    params.append('appointmentId', bookingId);
    params.append('userId', user.zhanghao);
    params.append('reason', reason);
    params.append('amount', amount);
    
    axios.post('/refund/apply', params)
      .then(response => {
        if (response.data.code === 0) {
          onRefundSubmitted();
        } else {
          setError(response.data.msg || t('booking.refundFailed'));
        }
        setSubmitting(false);
      })
      .catch(err => {
        console.error('退款申请提交出错:', err);
        setError(t('booking.refundFailed'));
        setSubmitting(false);
      });
  };

  return (
    <div className="refund-form">
      <h3>{t('booking.refundApplication')}</h3>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>{t('booking.refundAmount')}</label>
          <input 
            type="number" 
            value={amount} 
            onChange={(e) => setAmount(parseFloat(e.target.value))} 
            min="0" 
            step="0.01" 
            required 
          />
        </div>
        <div className="form-group">
          <label>{t('booking.refundReason')}</label>
          <textarea 
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('booking.refundReasonPlaceholder')}
            rows={4}
            required
          />
        </div>
        <div className="form-buttons">
          <button type="button" className="cancel-btn" onClick={onCancel}>
            <i className="fas fa-times"></i> {t('common.cancel')}
          </button>
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? <><i className="fas fa-spinner fa-spin"></i> {t('payment.processing')}</> : 
              <><i className="fas fa-paper-plane"></i> {t('booking.submitRefund')}</>}
          </button>
        </div>
      </form>
    </div>
  );
};

// 退款状态显示组件
const RefundStatus = ({ status, reason, response }) => {
  const { t } = useTranslation();
  const getStatusText = (status) => {
    switch(status) {
      case 0: return t('booking.refundNone');
      case 1: return t('booking.refundProcessing');
      case 2: return t('booking.refundApproved');
      case 3: return t('booking.refundRejected');
      default: return t('payment.unknownStatus');
    }
  };
  
  const getStatusClass = (status) => {
    switch(status) {
      case 1: return 'status-pending';
      case 2: return 'status-approved';
      case 3: return 'status-rejected';
      default: return '';
    }
  };
  
  return (
    <div className="refund-status">
      <div className={`status ${getStatusClass(status)}`}>
        <i className={status === 2 ? "fas fa-check" : status === 3 ? "fas fa-times" : "fas fa-clock"}></i>
        {getStatusText(status)}
      </div>
      {reason && (
        <div className="refund-reason">
          <strong>{t('booking.refundReason')}:</strong> {reason}
        </div>
      )}
      {response && (
        <div className="refund-response">
          <strong>{t('booking.refundResponse')}:</strong> {response}
        </div>
      )}
    </div>
  );
};

const UserAppointment = () => {
  const { user } = useAuth(); // 从 AuthContext 获取用户信息
  const [yuyueRecords, setYuyueRecords] = useState([]); // 预约记录
  const [error, setError] = useState(''); // 错误提示
  const [loading, setLoading] = useState(false); // 加载状态
  const [comments, setComments] = useState({}); // 存储每个预约的评论，使用预约ID作为键
  const [showCommentForm, setShowCommentForm] = useState({}); // 控制每个预约的评论表单显示
  const [editingComment, setEditingComment] = useState(null); // 当前正在编辑的评论
  const [showRefundForm, setShowRefundForm] = useState({}); // 控制每个预约的退款表单显示
  const [refundStatuses, setRefundStatuses] = useState({}); // 存储每个预约的退款状态
  // 修改预约相关
  const [showEditForm, setShowEditForm] = useState({}); // { [bookingId]: boolean }
  const [editForms, setEditForms] = useState({}); // { [bookingId]: { coachId, coachName, availableTimes: [], selectedTime, loadingTimes: false } }
  const [coaches, setCoaches] = useState([]);
  const navigate = useNavigate();

  console.log("用户信息:", user);
    
      const { t } = useTranslation();

  // 获取用户预约记录
  useEffect(() => {
    if (user?.zhanghao) {
      console.log("正在获取用户预约记录，账号:", user.zhanghao);
      setLoading(true);
      
      axios
        .get(`/jiaolianyuyue/${user.zhanghao}/yuyue`)
        .then((response) => {
          console.log("预约记录响应:", response);
          if (response.data.code === 0) {
            const records = response.data.data || [];
            setYuyueRecords(records);
            
            // 获取每个预约的评论
            records.forEach(record => {
              fetchComments(record.id);
              fetchRefundStatus(record.id);
            });
          } else {
            setError(response.data.msg || t('booking.fetchAppointmentsFailed'));
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('获取预约记录出错:', err);
          setError(t('booking.fetchAppointmentsError'));
          setLoading(false);
        });
    }
  }, [user]);

  // 获取教练列表（用于修改预约时更换教练）
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const res = await axios.get('/jiaolian/list?page=1&limit=1000');
        if (res.data && res.data.code === 0) {
          setCoaches(res.data.data.list || []);
        }
      } catch (e) {
        console.error('获取教练列表失败:', e);
      }
    };
    fetchCoaches();
  }, []);

  // 获取预约的评论
  const fetchComments = (bookingId) => {
    axios.get(`/jiaolianyuyue/comment/${bookingId}`)
      .then(response => {
        if (response.data.code === 0) {
          setComments(prev => ({
            ...prev,
            [bookingId]: response.data.data || []
          }));
        }
      })
      .catch(error => {
        console.error(`获取预约 ${bookingId} 的评论失败:`, error);
      });
  };

  // 获取预约的退款状态
  const fetchRefundStatus = (appointmentId) => {
    axios.get(`/jiaolianyuyue/refundStatus/${appointmentId}`)
      .then(response => {
        if (response.data.code === 0) {
          setRefundStatuses(prev => ({
            ...prev,
            [appointmentId]: {
              status: response.data.refundStatus || 0,
              reason: response.data.refundReason,
              response: response.data.refundResponse
            }
          }));
        }
      })
      .catch(error => {
        console.error(`获取预约 ${appointmentId} 的退款状态失败:`, error);
      });
  };

  // 删除评论
  const handleDeleteComment = (commentId, bookingId) => {
    axios.delete(`/jiaolianyuyue/comment/${commentId}?userId=${user.zhanghao}`)
      .then(response => {
        if (response.data.code === 0) {
          // 成功删除后刷新评论列表
          fetchComments(bookingId);
        } else {
          alert(response.data.msg || t('comments.deleteFailed'));
        }
      })
      .catch(error => {
        console.error('删除评论失败:', error);
        alert(t('comments.deleteError'));
      });
  };

  // 开始编辑评论
  const handleEditComment = (comment, bookingId) => {
    setEditingComment({
      ...comment,
      bookingId
    });
    setShowCommentForm(prev => ({
      ...prev,
      [bookingId]: true
    }));
  };

  // 取消编辑评论
  const handleCancelEdit = (bookingId) => {
    setEditingComment(null);
    setShowCommentForm(prev => ({
      ...prev,
      [bookingId]: false
    }));
  };

  // 切换评论表单显示
  const toggleCommentForm = (bookingId) => {
    // 如果已有正在编辑的评论，先清除
    if (editingComment) {
      setEditingComment(null);
    }
    
    setShowCommentForm(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  // 评论提交后的回调
  const handleCommentSubmitted = (bookingId) => {
    fetchComments(bookingId);
    setShowCommentForm(prev => ({
      ...prev,
      [bookingId]: false
    }));
    setEditingComment(null); // 清除编辑状态
  };

  // 切换退款表单显示
  const toggleRefundForm = (bookingId) => {
    setShowRefundForm(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  // 退款申请提交后的回调
  const handleRefundSubmitted = (bookingId) => {
    fetchRefundStatus(bookingId);
    setShowRefundForm(prev => ({
      ...prev,
      [bookingId]: false
    }));
  };

  // 是否可修改：预约开始时间需大于当前时间 24 小时
  const canModify = (record) => {
    const start = record.startTime || record.yuyueshijian;
    if (!start) return false;
    const startMs = typeof start === 'number' ? (start < 1e12 ? start * 1000 : start) : new Date(start).getTime();
    if (isNaN(startMs)) return false;
    const nowPlus24h = Date.now() + 24 * 60 * 60 * 1000;
    return startMs - nowPlus24h > 0;
  };

  // 打开/关闭修改表单
  const toggleEditForm = async (record) => {
    const id = record.id;
    setShowEditForm(prev => ({ ...prev, [id]: !prev[id] }));
    // 初始化表单并拉取可用时间
    if (!showEditForm[id]) {
      const coachId = record.jiaolianbianhao; // 使用教练编号作为排班查询ID
      const coachName = record.jiaolianxingming;
      await fetchAvailableTimes(id, coachId, record.duration || 60);
      setEditForms(prev => ({
        ...prev,
        [id]: {
          coachId,
          coachName,
          availableTimes: prev[id]?.availableTimes || [],
          selectedTime: prev[id]?.selectedTime || undefined,
          loadingTimes: false
        }
      }));
    }
  };

  // 拉取可用开始时间，并过滤 > 24 小时
  const fetchAvailableTimes = async (bookingId, coachId, durationMinutes) => {
    setEditForms(prev => ({
      ...prev,
      [bookingId]: { ...(prev[bookingId] || {}), loadingTimes: true }
    }));
    try {
      const form = new URLSearchParams();
      form.append('coachId', coachId);
      form.append('durationMinutes', durationMinutes);
      const res = await axios.post('/schedule/getAvailableStartTimes', form, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      if (res.data && res.data.code === 0) {
        const list = res.data.getAvailableStartTimes || [];
        const cutoff = Date.now() + 24 * 60 * 60 * 1000;
        const filtered = list.filter((t) => {
          const ms = typeof t === 'number' ? (t < 1e12 ? t * 1000 : t) : new Date(t).getTime();
          return ms > cutoff;
        });
        setEditForms(prev => ({
          ...prev,
          [bookingId]: {
            ...(prev[bookingId] || {}),
            availableTimes: filtered,
            selectedTime: filtered[0],
            loadingTimes: false
          }
        }));
      } else {
        setEditForms(prev => ({
          ...prev,
          [bookingId]: { ...(prev[bookingId] || {}), availableTimes: [], selectedTime: undefined, loadingTimes: false }
        }));
      }
    } catch (e) {
      console.error('获取可用时间失败:', e);
      setEditForms(prev => ({
        ...prev,
        [bookingId]: { ...(prev[bookingId] || {}), availableTimes: [], selectedTime: undefined, loadingTimes: false }
      }));
    }
  };

  // 教练切换
  const handleCoachChange = async (bookingId, coachId) => {
    const coach = coaches.find(c => String(c.jiaolianbianhao) === String(coachId) || String(c.id) === String(coachId));
    setEditForms(prev => ({
      ...prev,
      [bookingId]: {
        ...(prev[bookingId] || {}),
        coachId,
        coachName: coach ? coach.jiaolianxingming : '',
      }
    }));
    // 重新拉取该教练的可用时间
    const duration = (yuyueRecords.find(r => r.id === bookingId)?.duration) || 60;
    await fetchAvailableTimes(bookingId, coachId, duration);
  };

  // 提交修改
  const submitEdit = async (record) => {
    const form = editForms[record.id];
    if (!form || !form.coachId || !form.selectedTime) return;
    try {
      const payload = {
        id: record.id,
        // 教练信息（允许更换）
        jiaolianbianhao: form.coachId,
        jiaolianxingming: form.coachName || record.jiaolianxingming,
        // 时间与时长（不可早于 24 小时后，已在候选中过滤）
        startTime: form.selectedTime,
        duration: record.duration || 60,
        // 不可更换项目：沿用原值
        projectId: record.projectId,
        xiangmufenlei: record.xiangmufenlei,
        xiangmumingcheng: record.xiangmumingcheng,
        // 其他保持不变
        zhanghao: record.zhanghao,
        xingming: record.xingming,
        shouji: record.shouji,
        price: record.price,
        paymentMethod: record.paymentMethod,
        paymentStatus: record.paymentStatus,
        status: record.status,
        sfsh: record.sfsh,
        yuyueshuoming: record.yuyueshuoming
      };
      const res = await axios.post('jiaolianyuyue/update', payload, { headers: { 'Content-Type': 'application/json' } });
      if (res.data && res.data.code === 0) {
        alert(t('booking.updateSuccess'));
        // 刷新列表
        setShowEditForm(prev => ({ ...prev, [record.id]: false }));
        // 重新拉取预约列表
        if (user?.zhanghao) {
          setLoading(true);
          axios.get(`/jiaolianyuyue/${user.zhanghao}/yuyue`).then((response) => {
            if (response.data.code === 0) {
              setYuyueRecords(response.data.data || []);
            }
            setLoading(false);
          }).catch(() => setLoading(false));
        }
      } else {
        alert(res.data?.msg || t('booking.updateFailed'));
      }
    } catch (e) {
      console.error('修改预约失败:', e);
      alert(t('booking.updateError'));
    }
  };

  const handleGoBack = () => {
    // 获取用户信息
    let userData = null;
    try {
      const storedUser = localStorage.getItem('userData') || localStorage.getItem('user');
      if (storedUser) {
        userData = JSON.parse(storedUser);
      }
    } catch (err) {
      console.error('Error parsing user data', err);
    }
    
    // 返回到用户详情页
    if (userData && userData.id) {
      navigate(`/users/${userData.id}`);
    } else {
      navigate('/user-center');
    }
  };

  if (!user) {
    return <div className="payment-page">{t('payment.pleaseLoginFirst')}</div>;
  }

  if (loading) {
    return <div className="payment-page">{t('table.loading')}</div>;
  }

  if (error) {
    return <div className="payment-page" style={{ color: 'red' }}>{error}</div>;
  }

  return (
    <div className="payment-page">
      <h1>{t('appointments.myAppointments')}</h1>
      {yuyueRecords.length === 0 ? (
        <p>{t('booking.noAppointmentRecords')}</p>
      ) : (
        <ul className='booking-list'>
          {yuyueRecords.map((record) => (
            <li key={record.id} className='booking-item'>
              <div className="booking-info">
                <div><strong>{t('booking.appointmentNumber')}:</strong> {record.id}</div>
                <div><strong>{t('booking.coach')}:</strong> {record.jiaolianxingming || record.jiaolianbianhao}</div>
                <div><strong>{t('booking.time')}:</strong> {formatDateSafe(record.startTime || record.yuyueshijian)}</div>
                <div><strong>{t('booking.price')}:</strong> ¥{record.price || t('payment.notSpecified')}</div>
                <div><strong>{t('booking.status')}:</strong> {record.sfsh === '是' ? t('booking.confirmed') : t('booking.pending')}</div>
                
                {/* 退款状态区域 */}
                {refundStatuses[record.id] && refundStatuses[record.id].status > 0 && (
                  <div className="refund-info">
                    <RefundStatus 
                      status={refundStatuses[record.id].status}
                      reason={refundStatuses[record.id].reason}
                      response={refundStatuses[record.id].response}
                    />
                  </div>
                )}
              </div>

              {/* 修改预约（仅限距离开始时间>24小时，且新时间需选择>24小时的技师空闲时间；可更换教练；不可更换项目） */}
              {canModify(record) && (
                <div className="edit-appointment">
                  {!showEditForm[record.id] ? (
                    <button
                      onClick={() => toggleEditForm(record)}
                      className="toggle-comment-btn"
                      style={{ marginBottom: '10px' }}
                    >
                      {t('booking.editAppointment')}
                    </button>
                  ) : (
                    <div className="edit-form">
                      {/* 教练选择 */}
                      <div className="form-row">
                        <label>{t('booking.changeCoach')}</label>
                        <select
                          value={editForms[record.id]?.coachId || ''}
                          onChange={(e) => handleCoachChange(record.id, e.target.value)}
                        >
                          <option value={record.jiaolianbianhao}>{t('booking.currentCoach')}: {record.jiaolianxingming || record.jiaolianbianhao}</option>
                          {coaches.map(coach => (
                            <option key={coach.id}
                                    value={coach.jiaolianbianhao}>
                              {coach.jiaolianxingming} ({coach.jiaolianbianhao})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* 可用时间 */}
                      <div className="form-row">
                        <label>{t('booking.selectNewStartTime')}</label>
                        <select
                          value={editForms[record.id]?.selectedTime || ''}
                          onChange={(e) => setEditForms(prev => ({
                            ...prev,
                            [record.id]: { ...(prev[record.id] || {}), selectedTime: e.target.value }
                          }))}
                        >
                          <option value="">{t('booking.selectTimePlaceholder')}</option>
                          {(editForms[record.id]?.availableTimes || []).map(t => (
                            <option key={String(t)} value={t}>{formatDateSafe(t)}</option>
                          ))}
                        </select>
                      </div>

                      {/* 项目信息（只读） */}
                      <div className="form-row">
                        <label>{t('booking.name')}</label>
                        <input type="text" value={`${record.xiangmumingcheng || ''}`} readOnly />
                      </div>

                      <div className="form-actions">
                        <button className="cancel-btn" onClick={() => toggleEditForm(record)}>{t('common.cancel')}</button>
                        <button className="submit-btn" onClick={() => submitEdit(record)}>{t('booking.submitModification')}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 退款申请按钮和表单 */}
              {record.sfsh === '是' && (!refundStatuses[record.id] || refundStatuses[record.id].status === 0) && (
                <div className="refund-action">
                  {!showRefundForm[record.id] ? (
                    <button 
                      onClick={() => toggleRefundForm(record.id)}
                      className="toggle-refund-btn"
                    >
                      {t('booking.refundApplication')}
                    </button>
                  ) : (
                    <RefundForm 
                      bookingId={record.id}
                      price={record.price}
                      onRefundSubmitted={() => handleRefundSubmitted(record.id)}
                      onCancel={() => toggleRefundForm(record.id)}
                    />
                  )}
                </div>
              )}
              
              {/* 评论区域 */}
              <div className="booking-comments">
                <h3>{t('booking.comment')}</h3>
                {comments[record.id] && comments[record.id].length > 0 ? (
                  comments[record.id].map(comment => (
                    <CommentDisplay 
                      key={comment.id} 
                      comment={comment} 
                      canManage={comment.userId === user.zhanghao}
                      onDelete={(commentId) => handleDeleteComment(commentId, record.id)}
                      onEdit={(comment) => handleEditComment(comment, record.id)}
                    />
                  ))
                ) : (
                  <p>{t('table.noData')}</p>
                )}
                
                {/* 评论按钮和表单 */}
                {record.sfsh === '是' && (
                  <div className="comment-action">
                    {!showCommentForm[record.id] ? (
                      <button 
                        onClick={() => toggleCommentForm(record.id)}
                        className="toggle-comment-btn"
                      >
                        {t('booking.addComment')}
                      </button>
                    ) : (
                      <>
                        {editingComment && editingComment.bookingId === record.id ? (
                          <CommentForm 
                            bookingId={record.id}
                            initialData={editingComment}
                            isEditing={true}
                            onCancel={() => handleCancelEdit(record.id)}
                            onCommentSubmitted={() => handleCommentSubmitted(record.id)} 
                          />
                        ) : (
                          <>
                            <button 
                              onClick={() => toggleCommentForm(record.id)}
                              className="cancel-comment-btn"
                            >
                              {t('common.cancel')}
                            </button>
                            <CommentForm 
                              bookingId={record.id} 
                              onCommentSubmitted={() => handleCommentSubmitted(record.id)} 
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <button onClick={handleGoBack} className="go-back-btn">
        <FontAwesomeIcon icon={faArrowLeft} /> {t('appointments.backToCenter')}
      </button>
    </div>
  );
};

export default UserAppointment;

